/**
 * Patient Onboarding Router
 * Manages the patient onboarding flow and routing
 */
import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { toast } from 'react-toastify';

// Onboarding Components
import PatientProfileForm from './PatientProfileForm';
import PatientBiometricsForm from './PatientBiometricsForm';
import PatientDocumentUpload from './PatientDocumentUpload';
import PatientIntakeForm from './PatientIntakeForm';
import PatientNextSteps from './PatientNextSteps';
import PatientWelcome from './PatientWelcome';

// API Service
import { patientOnboardingService } from '../../services/api/patientOnboarding';

// Auth Context
import { useAuth } from '../../hooks/useAuth';

const OnboardingRouter = () => {
  const { user, fetchProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [onboardingState, setOnboardingState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for intent parameter (from magic links)
  const intent = searchParams.get('intent');

  useEffect(() => {
    fetchOnboardingState();
  }, []);

  const fetchOnboardingState = async () => {
    try {
      setLoading(true);
      const state = await patientOnboardingService.getOnboardingState();
      setOnboardingState(state);
      setError(null);
    } catch (err) {
      console.error('Error fetching onboarding state:', err);
      setError('Failed to load onboarding information. Please try again.');
      toast.error('Failed to load onboarding information');
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSaved = async () => {
    // Refresh onboarding state after profile save
    await fetchOnboardingState();
    
    // Show success message
    toast.success('Profile saved successfully!');
    
    // Navigate based on completion
    if (onboardingState?.stage === 'complete') {
      navigate('/onboarding/patient/finish');
    } else {
      navigate('/onboarding/patient/next-steps');
    }
  };

  const handleSectionComplete = async (sectionKey) => {
    try {
      await patientOnboardingService.completeSection(sectionKey);
      await fetchOnboardingState();
      toast.success(`${sectionKey} section completed!`);
    } catch (err) {
      console.error('Error completing section:', err);
      toast.error('Failed to update progress');
    }
  };

  const handleSkipToDashboard = async () => {
    console.log('🔄 handleSkipToDashboard called');
    console.log('🔍 Current onboarding state:', onboardingState);
    console.log('🔍 Current user state:', user);
    
    try {
      // Refresh user profile to get updated patient_profile_completed status
      console.log('📡 Refreshing user profile...');
      await fetchProfile();
      console.log('✅ User profile refreshed');
      
      // Refresh onboarding state to reflect any changes
      console.log('📡 Refreshing onboarding state...');
      await fetchOnboardingState();
      console.log('✅ Onboarding state refreshed');
    } catch (error) {
      console.error('❌ Error refreshing state:', error);
    }
    
    console.log('🔄 Navigating to patient dashboard...');
    navigate('/patient-dashboard');
  };

  // Loading state
  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="60vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box p={3}>
        <Alert 
          severity="error" 
          action={
            <button onClick={fetchOnboardingState}>
              Retry
            </button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  // Determine default route based on onboarding state
  const getDefaultRoute = () => {
    if (!onboardingState) return '/onboarding/patient/profile';
    
    const { stage, sections_done } = onboardingState;
    
    // If profile section is not complete, go to profile form
    if (!sections_done?.profile) {
      return '/onboarding/patient/profile';
    }
    
    // If profile is complete but other sections remain, show next steps
    if (stage === 'minimal_complete') {
      return '/onboarding/patient/next-steps';
    }
    
    // If everything is complete, show finish page
    if (stage === 'complete') {
      return '/onboarding/patient/finish';
    }
    
    // Default fallback
    return '/onboarding/patient/profile';
  };

  return (
    <Box>
      {/* Trial Banner */}
      {onboardingState?.trial?.is_active && (
        <Alert 
          severity="info" 
          sx={{ mb: 2 }}
        >
          Welcome to MindWell! You have {onboardingState.trial.days_remaining} days 
          of free access to explore all features.
        </Alert>
      )}

      <Routes>
        {/* Default redirect based on state */}
        <Route 
          path="/" 
          element={<Navigate to={getDefaultRoute()} replace />} 
        />
        
        {/* Profile Form */}
        <Route 
          path="/profile" 
          element={
            <PatientProfileForm 
              onSaved={handleProfileSaved}
              onSkip={handleSkipToDashboard}
              initialData={onboardingState}
            />
          } 
        />
        
        {/* Biometrics Form */}
        <Route 
          path="/biometrics" 
          element={
            <PatientBiometricsForm 
              onSaved={() => handleSectionComplete('biometrics')}
              onSkip={handleSkipToDashboard}
              initialData={onboardingState}
            />
          } 
        />
        
                {/* Document Upload */}
        <Route
          path="/documents"
          element={
            <PatientDocumentUpload
              onSaved={() => handleSectionComplete('uploads')}
              onSkip={handleSkipToDashboard}
              initialData={onboardingState}
            />
          }
        />
        
        {/* Intake Form */}
        <Route
          path="/intake"
          element={
            <PatientIntakeForm
              onSaved={() => handleSectionComplete('intake')}
              onSkip={handleSkipToDashboard}
              initialData={onboardingState}
            />
          }
        />
        
        {/* Next Steps */}
        <Route 
          path="/next-steps" 
          element={
            <PatientNextSteps 
              onboardingState={onboardingState}
              onSectionComplete={handleSectionComplete}
              onContinueToDashboard={handleSkipToDashboard}
            />
          } 
        />
        
        {/* Emergency Contact (stub for future) */}
        <Route 
          path="/emergency" 
          element={
            <Box p={3}>
              <Alert severity="info">
                Emergency contact form will be available soon. 
                You can add this information from your profile later.
              </Alert>
            </Box>
          } 
        />
        
        {/* Medical History (stub for future) */}
        <Route 
          path="/history" 
          element={
            <Box p={3}>
              <Alert severity="info">
                Medical history form will be available soon. 
                You can add this information from your dashboard later.
              </Alert>
            </Box>
          } 
        />
        
        {/* Welcome/Finish */}
        <Route 
          path="/finish" 
          element={
            <PatientWelcome 
              onboardingState={onboardingState}
              onGoToDashboard={handleSkipToDashboard}
            />
          } 
        />
        
        {/* Fallback */}
        <Route 
          path="*" 
          element={<Navigate to={getDefaultRoute()} replace />} 
        />
      </Routes>
    </Box>
  );
};

export default OnboardingRouter;
